import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, lastValueFrom, Observable, of } from 'rxjs';
import { Notificacion } from '../models/Notificacion';
import { UsuarioService } from './usuario.service';
import UAParser from 'ua-parser-js';
import { NotificacionPushToken } from '../models/NotificationPushToken';
import { API_URI, VAPID_PUBLIC_KEY } from 'src/environments/environment';
import { Platform } from '@angular/cdk/platform';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';
import { IS_NATIVE } from './../shared/platform.util';
import { SwPush } from '@angular/service-worker';
//import { LocalNotifications } from '@capacitor/local-notifications';

@Injectable({
  providedIn: 'root'
})
export class NotificacionService {
  private readonly _usuarioService = inject(UsuarioService);
  private readonly _router = inject(Router);
  private readonly _swPush = inject(SwPush, { optional: true }); // OPCIONAL
  private readonly API_URI: string = API_URI + 'app';
  notificaciones: Notificacion[] = [];
  isNewNotification = new BehaviorSubject<boolean>(false);
  notificacionPushToken: NotificacionPushToken = {};
  modalPwaEvent: any;
  modalPwaPlatform: string|undefined;
  private notificationListenersAlreadySet = false;
  private _showNotifications = new BehaviorSubject<boolean>(false);
  public showNotifications$ = this._showNotifications.asObservable();

  // refresco del estado de notificaciones sin depender del push
  private readonly POLL_INTERVAL_MS = 45000;
  private pollingAlreadySet = false;
  private pollHandle?: ReturnType<typeof setInterval>;
  private appStateListenerHandle?: PluginListenerHandle;

  constructor(private http: HttpClient, private platform: Platform,) {
    const parser = new UAParser();
    const result = parser.getResult();
    if(result.browser.name){
      this.notificacionPushToken.x_browser_name = result.browser.name;
    }
    if(result.browser.version){
      this.notificacionPushToken.x_browser_version = result.browser.version;
    }
    if(result.device.vendor){
      this.notificacionPushToken.x_device_vendor = result.device.vendor;
    }
    if(result.device.model){
      this.notificacionPushToken.x_device_model = result.device.model;
    }
    if(result.device.type){
      this.notificacionPushToken.x_device_type = result.device.type;
    }
    if(result.os.name){
      this.notificacionPushToken.x_os_name = result.os.name;
    }
    if(result.os.version){
      this.notificacionPushToken.x_os_version = result.os.version;
    }
    if (this.platform.ANDROID) {
      window.addEventListener('beforeinstallprompt', (event: any) => {
        event.preventDefault();
        this.modalPwaEvent = event;
      });
    }
  }

  setShowNotifications(valor: boolean) {
    this._showNotifications.next(valor);
  }

  getShowNotifications(): boolean {
    return this._showNotifications.getValue();
  }

  async verificarEstadoNotificaciones(id_usuario: string) {
    const yaRegistrado = localStorage.getItem('notificacionesPushRegistradas') === 'true';
    const showNotifications = localStorage.getItem('showNotifications') === '0';

    if (yaRegistrado || showNotifications) {
      this.setShowNotifications(false);
      return;
    }
    
    // Si no está marcado como registrado, hace la llamada al backend
    this.getNotificationesPushToken(id_usuario).subscribe({
      next: (res) => {
        const tokenNotiPush = (localStorage.getItem('tokenNotiPush')) ? JSON.parse(localStorage.getItem('tokenNotiPush')!) : null;
        const tokenFcm = localStorage.getItem('tokenFcmPush') ?? null;
        const algunoActivo = res.some(d => this._usuarioService.usuario().id === d.id_usuario &&
      ((tokenNotiPush &&
        tokenNotiPush.keys &&
        tokenNotiPush.endpoint === d.x_webpush_endpoint && 
        tokenNotiPush.keys["auth"] === d.x_webpush_keys_auth && 
        tokenNotiPush.keys["p256dh"] === d.x_webpush_keys_p256dh) ||
      (tokenFcm && tokenFcm === d.x_fcm_token)));
        if (algunoActivo) {
          this.setShowNotifications(false);
        } else {
          this.setShowNotifications(true);
        }
      },
      error: (e) => console.error("Error al obtener las Notificaciones Push Token del usuario ", JSON.stringify(e)),
      complete: () => console.log("Obtener Notificaciones Push Token del usuario completada")
    })
  }

  obtenerNotificaciones(){
    this.getNotificaciones(this._usuarioService.usuario().id!).subscribe({
      next: (res) =>  {
        if(res.filter(val => !val.b_visto).length > 0){
          this.isNewNotification.next(true);
        }else{
          this.isNewNotification.next(false);
        }
        this.notificaciones = res.map(no => ({
          ...no,
          b_visto: !!no.b_visto // convierte 1 → true y 0 → false
        }));
      },
      error: (e) => e.status != 404 ? console.log(e) : console.log(e.error.message),
      complete: () => console.log("Notificaciones del usuario obtenidas"),
    });
  }

  validarNotificacionPushToken(sub: PushSubscription): Observable<boolean> {
    const json = sub.toJSON();
    if (!json.keys) {
      throw new Error('La suscripción no contiene claves públicas.');
    }
    const notificacionPushToken: NotificacionPushToken = {
      id_usuario: this._usuarioService.usuario().id,
      x_webpush_endpoint: sub.endpoint,
      x_webpush_keys_auth: json.keys["auth"],
      x_webpush_keys_p256dh: json.keys["p256dh"],
    };

    return new Observable<boolean>((observer) => {
      this.getNotificationPushTokenXToken(notificacionPushToken).subscribe({
        next: (res: NotificacionPushToken[]) => {
          const exists = res.some((t) => 
            t.id_usuario === notificacionPushToken.id_usuario &&
            t.x_webpush_endpoint === notificacionPushToken.x_webpush_endpoint &&
            t.x_webpush_keys_auth === notificacionPushToken.x_webpush_keys_auth &&
            t.x_webpush_keys_p256dh === notificacionPushToken.x_webpush_keys_p256dh
          );

          if (!exists) {
            this.createNotificationPushToken(notificacionPushToken).subscribe({
              next: () => observer.next(true),
              error: (e) => observer.error(e),
              complete: () => observer.complete()
            });
          } else {
            observer.next(true);
            observer.complete();
          }
        },
        error: (e) => observer.error(e),
      });
    });
  }

  createNotificationPushToken(notificacionPushToken: NotificacionPushToken): Observable<any>{
    // El token pertenece a esta app (Libre Salud), no al usuario en general;
    // así el backend sabe a qué app pushear cuando la comunicación es solo para una.
    notificacionPushToken.x_app = 'libresalud';
    const parser = new UAParser();
    const result = parser.getResult();
    if(!notificacionPushToken.x_browser_name || notificacionPushToken.x_browser_name == ""){
      notificacionPushToken.x_browser_name = result.browser.name
    }
    if(!notificacionPushToken.x_browser_version || notificacionPushToken.x_browser_version == ""){
      notificacionPushToken.x_browser_version = result.browser.version;
    }
    if(!notificacionPushToken.x_device_vendor || notificacionPushToken.x_device_vendor == ""){
      notificacionPushToken.x_device_vendor = result.device.vendor;
    }
    if(!notificacionPushToken.x_device_model || notificacionPushToken.x_device_model == ""){
      notificacionPushToken.x_device_model = result.device.model;
    }
    if(!notificacionPushToken.x_device_type || notificacionPushToken.x_device_type == ""){
      notificacionPushToken.x_device_type = result.device.type;
    }
    if(!notificacionPushToken.x_os_name || notificacionPushToken.x_os_name == ""){
      notificacionPushToken.x_os_name = result.os.name;
    }
    if(!notificacionPushToken.x_os_version || notificacionPushToken.x_os_version == ""){
      notificacionPushToken.x_os_version = result.os.version;
    }
    return this.http.post<any>(`${this.API_URI}/notificaciones/pushToken`, notificacionPushToken);
  }

  private getNotificaciones(id_usuario: string): Observable<Notificacion[]>{
    return this.http.get<Notificacion[]>(`${this.API_URI}/notificaciones/xUsuario/${id_usuario}?x_app=libresalud`);
  }

  getNotificacion(id: number): Observable<Notificacion | null>{
    return this.http
      .get<Notificacion>(`${this.API_URI}/notificaciones/xId/${id}`)
      .pipe(catchError(() => of(null)));
  }

  getNotificationPushTokenXToken(notificacionPushToken: NotificacionPushToken): Observable<NotificacionPushToken[]>{
    return this.http.post<NotificacionPushToken[]>(`${this.API_URI}/notificaciones/pushToken/xToken`, notificacionPushToken);
  }

  getNotificationesPushToken(id_usuario: string): Observable<NotificacionPushToken[]>{
    return this.http.get<NotificacionPushToken[]>(`${this.API_URI}/notificaciones/pushToken/${id_usuario}`);
  }

  createNotificacion(notificacion: Notificacion): Observable<void>{
    return this.http.post<void>(`${this.API_URI}/notificaciones`, notificacion);
  }

  createNotificacionBienvenida(): Observable<void>{
    const notificacion: Notificacion = {
      id_usuario: this._usuarioService.usuario().id,
      x_titulo: 'Hola!!',
      x_descripcion: '¡Gracias por crear tu cuenta en Libre Salud!',
      x_app: 'libresalud'
    }
    return this.http.post<void>(`${this.API_URI}/notificaciones`, notificacion);
  }

  createNotificacionOrdenComercioResponse(id_cliente: string, x_nombre_fantasia: string, id_ordenComercio: number): Observable<void>{
    const notificacion: Notificacion = {
      id_usuario: id_cliente,
      x_titulo: 'Respuesta de compra #' + id_ordenComercio,
      x_descripcion: x_nombre_fantasia + ' ha respondido. Finaliza la compra.',
      x_app: 'libresalud'
    }
    return this.http.post<void>(`${this.API_URI}/notificaciones`, notificacion);
  }

  createNotificacionOrdenComercioConfirm(id_comercio: string, id_ordenComercio: number): Observable<void>{
    const notificacion: Notificacion = {
      id_usuario: id_comercio,
      x_titulo: 'Realizada compra #' + id_ordenComercio,
      x_descripcion: 'La orden de compra se ha confirmado.',
      x_app: 'libresalud'
    }
    return this.http.post<void>(`${this.API_URI}/notificaciones`, notificacion);
  }

  deleteNotificacionPushToken(id: number): Observable<void>{
    return this.http.delete<void>(`${this.API_URI}/notificaciones/pushToken/${id}`);
  }

  private deleteNotificationPushTokenXToken(notificacionPushToken: Partial<NotificacionPushToken>): Observable<any>{
    return this.http.post<any>(`${this.API_URI}/notificaciones/pushToken/deleteXToken`, notificacionPushToken);
  }

  sendPushPrueba(notificacionPushToken: NotificacionPushToken): Observable<void>{
    return this.http.post<void>(`${this.API_URI}/notificaciones/sendPushPrueba`, notificacionPushToken);
  }

  updateNotificacion(notificacion: Notificacion): Observable<void>{
    delete notificacion.d_fecha;
    if(this.notificaciones.filter(val => !val.b_visto).length > 0){
      this.isNewNotification.next(true);
    }else{
      this.isNewNotification.next(false);
    }
    return this.http.put<void>(`${this.API_URI}/notificaciones/${notificacion.id}`, notificacion);
  }

  getPlatform(): string {
    // document.getElementById('centerFrame')!.style.filter = 'blur(10px)';
    console.log(this.platform);
    if (this.platform.ANDROID) {
      return 'ANDROID';
    }

    if (this.platform.IOS && this.platform.SAFARI) {
      const isInStandaloneMode = ('standalone' in window.navigator) && ((<any>window.navigator)['standalone']);
      if (!isInStandaloneMode) {
        return 'IOS';
      }
    }

    return 'OTHER';
  }

  public addToHomeScreen(): Promise<boolean> {
    return new Promise((resolved, reject) => {
      this.modalPwaEvent.prompt();
      this.modalPwaEvent.userChoice
      .then((choiceResult: { outcome: string; }) => {
        if (choiceResult.outcome === 'accepted') {
          resolved(true);
        } else {
          resolved(false);
        }
      })
      .catch((error: any) => {
        reject(error);
      });
    });
  }

  // refresca "hay notificación nueva" al volver del segundo plano y cada tanto
  // mientras la app está abierta, sin depender de que llegue el push
  startNotificationsPolling(): void {
    if (this.pollingAlreadySet) return;
    this.pollingAlreadySet = true;

    if (IS_NATIVE) {
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) this.obtenerNotificaciones();
      }).then(handle => this.appStateListenerHandle = handle);
    }

    this.pollHandle = setInterval(() => this.obtenerNotificaciones(), this.POLL_INTERVAL_MS);
  }

  stopNotificationsPolling(): void {
    this.pollingAlreadySet = false;

    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = undefined;
    }
    if (this.appStateListenerHandle) {
      this.appStateListenerHandle.remove();
      this.appStateListenerHandle = undefined;
    }
  }

  async initNotificaciones() {
    this.startNotificationsPolling();

    try {
      console.log("📡 Iniciando notificaciones push...");
      const permisosConcedidos = await this.registerForPushNotifications();
      if (!permisosConcedidos) {
        console.warn("⛔ Permisos de notificaciones no concedidos, no se configuran listeners.");
        return;
      }
      await this.setupNotificationListeners();
    } catch (err) {
      console.error('❌ Error al iniciar notificaciones', err);
    }
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  }

  private withTimeout<T>(promise: Promise<T>, ms = 10000): Promise<T> {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error(`Timeout luego de ${ms} ms`)), ms)
      ),
    ]);
  }

  async registerForPushNotifications(): Promise<boolean> {
    try {
      if (IS_NATIVE) {
        // ===============================
        //        FLUJO NATIVO (APP)
        // ===============================
        // 1) Pedir permisos
        const perm = await PushNotifications.requestPermissions();
        if (perm.receive !== 'granted') {
          // Intento de ayuda: abrir ajustes del sistema (dinámico)
          try {
            const { NativeSettings, AndroidSettings, IOSSettings } = await import('capacitor-native-settings');
            const abrir = window.confirm('Para activar las notificaciones, habilitalas desde la configuración del sistema. ¿Querés abrir la configuración ahora?');
            if (abrir) {
              if (Capacitor.getPlatform() === 'ios') {
                await NativeSettings.openIOS({ option: IOSSettings.App });
              } else {
                await NativeSettings.openAndroid({ option: AndroidSettings.ApplicationDetails });
              }
              // Reintento
              const reperm = await PushNotifications.requestPermissions();
              if (reperm.receive !== 'granted') return false;
            } else {
              return false;
            }
          } catch {
            // Plugin no disponible: simple mensaje y salir
            console.warn('No se pudo abrir la configuración del sistema.');
            return false;
          }
        }

        // 2) Registrar — el token FCM llega por el evento 'registration' (ya escuchando)
        await PushNotifications.register();
        // Marcar como registrado de forma optimista para que el banner no vuelva a aparecer
        // El listener 'registration' actualizará el token real en localStorage y backend
        localStorage.setItem('notificacionesPushRegistradas', 'true');
        localStorage.setItem('showNotifications', '0');
        this.setShowNotifications(false);
        return true;

      } else {

        if (!this._swPush?.isEnabled) {
          this.setShowNotifications(false);
          localStorage.setItem('showNotifications', '0');
          return false;
        }

        const ensureGranted = async (): Promise<boolean> => {
          if (typeof Notification === 'undefined') return false;
          if (Notification.permission === 'granted') return true;

          const req = await Notification.requestPermission();
          return req === 'granted';
        };

        const granted = await ensureGranted();

        if (!granted) {
          return false;
        }
        const reg = await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();

        if (existing) {
          await existing.unsubscribe();
        }

        const sub = await this.withTimeout(
          reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource
          }),
          10000
        );

        const serialized = JSON.stringify(sub);
        const prev = localStorage.getItem('tokenNotiPush');
        const prevUserId = localStorage.getItem('tokenNotiPushUserId');

        const uObj = typeof this._usuarioService.usuario === 'function'
          ? this._usuarioService.usuario()
          : this._usuarioService.usuario;

        const idUsuario = uObj?.id;

        // ademas de comparar el token, comparamos el usuario: el mismo navegador/dispositivo
        // puede tener el mismo token de push aunque haya cambiado el usuario logueado, y en
        // ese caso igual hay que volver a registrarlo (para ESE usuario)
        if (prev !== serialized || prevUserId !== idUsuario) {
          localStorage.setItem('tokenNotiPush', serialized);
          localStorage.setItem('tokenNotiPushUserId', idUsuario ?? '');
          localStorage.setItem('checkNotificacionesPush', 'true');
          localStorage.setItem('notificacionesPushRegistradas', 'true');
          localStorage.setItem('showNotifications', '0');

          const json = sub.toJSON();
          if (!json.keys) throw new Error('La suscripción no contiene claves públicas.');

          if (!idUsuario) {
          } else {
            try {
              await lastValueFrom(this.createNotificationPushToken({
                id_usuario: idUsuario,
                x_webpush_endpoint: sub.endpoint,
                x_webpush_keys_auth: json.keys['auth'],
                x_webpush_keys_p256dh: json.keys['p256dh']
              }));
            } catch (error: any) {
              const code = error?.error?.error?.code;
              const http = error?.status;

              if (code === 'ER_DUP_ENTRY' || http === 409) {
                console.warn('ℹ️ Token ya estaba registrado.');
              } else {
                throw error;
              }
            }
          }
        } else {
          console.log('R36 - token igual al previo');
        }

        localStorage.setItem('notificacionesPushRegistradas', 'true');
        localStorage.setItem('showNotifications', '0');
        this.setShowNotifications(false);
        return true;
      }
    } catch (err: any) {
      // ===== Manejo de errores (mensajes como los que ya tenías) =====
      const msg = err?.toString?.() || '';

      if (msg.includes('NotAllowedError: User denied permission to use the Push API.')) { // PC
        alert('Las notificaciones están bloqueadas\nAbra las preferencias de su navegador o haga clic en el candado 🔒 cerca de la barra de direcciones para cambiar sus preferencias de notificación.');
      } else if (msg.includes('NotAllowedError: User denied push permission')) { // IPHONE
        alert('Las notificaciones están bloqueadas\nAbra las preferencias de su dispositivo para habilitar las notificaciones.');
      } else if (msg.includes('NotAllowedError: Registration failed - permission denied')) { // ANDROID
        alert('Las notificaciones están bloqueadas\nAbra las preferencias de su dispositivo para habilitar las notificaciones.');
      } else if (msg.includes('Service workers are disabled') || msg.includes('not supported')) { // PC
        alert('Las notificaciones no son compatibles en este dispositivo.');
        localStorage.setItem('checkNotificacionesPush', 'true');
      } else {
        console.error("❌ Error al registrar para push notifications", err);
      }
      return false;
    }
  }

  async setupNotificationListeners() {
    if (!IS_NATIVE) {
      console.warn('PushNotifications no disponible en web');
      return;
    }
    if (this.notificationListenersAlreadySet) {
      console.warn("Los listeners de notificaciones ya están configurados");
      return;
    }
    this.notificationListenersAlreadySet = true;
    await PushNotifications.removeAllListeners();

    // 3. Registrar y verificar token
      await PushNotifications.addListener('registration', async (token: Token) => {
        try {
          console.log('✅ Token de notificación registrado:', token.value);
          const savedToken = localStorage.getItem('tokenFcmPush');
          const savedUserId = localStorage.getItem('tokenFcmPushUserId');
          const idUsuario = this._usuarioService.usuario().id;

          // el token de push es del dispositivo/instalación, no del usuario logueado en la app:
          // si en este mismo celular se logueó otro usuario, el token puede llegar igual, pero
          // igual hay que registrarlo para el usuario actual
          if (savedToken !== token.value || savedUserId !== idUsuario) {
            localStorage.setItem('tokenFcmPush', token.value);
            localStorage.setItem('tokenFcmPushUserId', idUsuario ?? '');
            localStorage.setItem('checkNotificacionesPush', 'true');

            await lastValueFrom(this.createNotificationPushToken({
              id_usuario: idUsuario,
              x_fcm_token: token.value
            }));
          }

          localStorage.setItem('notificacionesPushRegistradas', 'true');
          localStorage.setItem('showNotifications', '0');
          this.setShowNotifications(false);
        } catch (e) {
          console.error("❌ Error en listener de registro de token:", JSON.stringify(e));
        }
      });

      // 4. Listener para errores de registro
      PushNotifications.addListener('registrationError', (error: any) => {
        console.error('❌ Error de registro de notificaciones:', error);
      });

      // 5. Listener para notificación recibida en primer plano
      PushNotifications.addListener('pushNotificationReceived', async (notification: PushNotificationSchema) => {
        console.log('🔔 Notificación recibida (foreground):', notification);
    
        //alert(notification.title + '\n' + notification.body);

        this.isNewNotification.next(true);

        /*await LocalNotifications.schedule({
          notifications: [{
            title: notification.title ?? 'Titulo',
            body: notification.body ?? 'Cuerpo de la notificación',
            id: Math.floor(Math.random() * 100000), // número único
            smallIcon: 'res://ic_launcher',
            iconColor: '#3880ff'
          }]
        });*/
      });

      // 6. Listener para notificación tocada: solo navega, no marca como
      // leída acá — eso pasa recién cuando el usuario entra efectivamente
      // a verla dentro de la app (lista o detalle).
      PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
        console.log('📲 Notificación tocada:', action);
        const data = action.notification.data;
        const id = data?.id ? Number(data.id) : null;
        const x_link: string | undefined = data?.x_link;

        if (x_link) {
          this._router.navigate([x_link]);
        } else if (id) {
          this._router.navigate(['/notificacion', id]);
        } else {
          this._router.navigate(['/notifications']);
        }
      });
  }

  // Revoca la asociación del token de push de este dispositivo con el usuario que se está
  // desloguando (sin borrar el token físico), para que un usuario que ya no usa este
  // dispositivo/navegador deje de recibir sus notificaciones push.
  // Importante: llamar ANTES de limpiar el token de autenticación (tokenId/tokenBackend),
  // porque esta request necesita ir autenticada.
  async revokeCurrentDeviceToken(): Promise<void> {
    try {
      const idUsuario = localStorage.getItem('tokenFcmPushUserId') || localStorage.getItem('tokenNotiPushUserId');
      if (!idUsuario) return;

      const fcmToken = localStorage.getItem('tokenFcmPush');
      if (fcmToken) {
        await lastValueFrom(this.deleteNotificationPushTokenXToken({ id_usuario: idUsuario, x_fcm_token: fcmToken }));
      }

      const webPushRaw = localStorage.getItem('tokenNotiPush');
      if (webPushRaw) {
        const sub = JSON.parse(webPushRaw);
        if (sub?.endpoint && sub?.keys?.auth && sub?.keys?.p256dh) {
          await lastValueFrom(this.deleteNotificationPushTokenXToken({
            id_usuario: idUsuario,
            x_webpush_endpoint: sub.endpoint,
            x_webpush_keys_auth: sub.keys.auth,
            x_webpush_keys_p256dh: sub.keys.p256dh
          }));
        }
      }

      localStorage.removeItem('tokenFcmPushUserId');
      localStorage.removeItem('tokenNotiPushUserId');
    } catch (e) {
      console.error('Error al revocar el token de push del dispositivo', e);
    }
  }

  clearData(){
    this.stopNotificationsPolling();
    this.notificacionPushToken = {};
    this.notificaciones = [];
  }
}
